import { z } from 'zod';

import { ComponentDefinition } from '../../types/ComponentDefinition';
import { ComponentPayloadValidator, ComponentValidator } from '../Component';
import { AbstractPort, Port } from '../fields/AbstractPort';
import { AC_EARTHED_CONDUCTORS, DC_EARTHED_CONDUCTORS } from '../fields/EarthedConductor';
import { GroundReferences } from '../fields/GroundReference';
import { BusGroundingConfiguration, isSymmetricalImpedance } from '../fields/GroundingConfiguration';
import { Resistance } from '../fields/Resistance';

const port = AbstractPort.extend({
    AC: Port.AC.default({}),
    DC: Port.DC.default({}),
}).default({ powerFlowDirection: 'bidirectional' });

const earthing = z
    .object({
        configuration: BusGroundingConfiguration.validator,
        impedance: Resistance.validators.value,
        groundReferences: GroundReferences.validator,
        line: z
            .union([z.enum(AC_EARTHED_CONDUCTORS), z.enum(DC_EARTHED_CONDUCTORS)])
            .nullable()
            .default(null),
    })
    .transform((earthing) => {
        const symmetrical = isSymmetricalImpedance(earthing.configuration);

        return {
            ...earthing,
            line: symmetrical ? null : earthing.line,
            groundReferences: {
                'L+': { ...earthing.groundReferences['L+'], referenced: symmetrical },
                'L-': { ...earthing.groundReferences['L-'], referenced: symmetrical },
            },
        };
    })
    .default({});

const BusBase = z.object({
    type: z.literal('bus').default('bus'),
    electrical: z
        .object({
            ports: z.array(port).min(1).default([undefined]),
            earthing,
        })
        .default({}),
});

export const BusValidator = ComponentValidator.merge(BusBase);
export const BusPayloadValidator = ComponentPayloadValidator.merge(BusBase);

export const BusDefinition: ComponentDefinition = {
    type: 'bus',
    name: 'Electrical Bus',
    plural: 'Electrical Buses',
    indicator: 'BUS',
    defaultColSpan: 2,
    hasDynamicNumberOfPorts: true,
    interoperabilityDataModel: false,
    ports: {
        min: 1,
        max: Infinity,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
    },
    actsAsConverter: false,
    actsAsBus: true,
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: BusValidator,
        payload: BusPayloadValidator,
    },
};

export type Bus = z.infer<typeof BusValidator>;
export type BusPayload = z.infer<typeof BusPayloadValidator>;
